import type { TableRowActionItem } from "@shared/components/UiTableRowActions/index.vue";
import type { TableGroupHeaderRow } from "@shared/types/tableGroupHeader";
import type { UiTagType } from "@shared/components/UiTag/index.vue";
import { t } from "@shared/utils/i18n";

export type VcWarehouseMappingKind = "PO" | "DF";

export const VC_WAREHOUSE_MAPPING_KIND_OPTIONS = [
    { label: t("PO仓"), value: "PO" as VcWarehouseMappingKind },
    { label: t("DF仓"), value: "DF" as VcWarehouseMappingKind },
];

export function getVcWarehouseMappingMatchTagType(matchTag: string): UiTagType {
    if (matchTag === t("匹配")) return "success";
    if (matchTag === t("地址不匹配")) return "error";
    if (matchTag === t("地址信息不全")) return "warning";
    return "neutral";
}

/** VC 仓库状态（1-启用，2-禁用），与 amazon-vc/warehouse 保持一致 */
export function getVcWarehouseMappingStatusTagType(status: number): UiTagType {
    return status === 1 ? "success" : "neutral";
}

export const VC_WAREHOUSE_MAPPING_COMBINED_SEARCH_OPTIONS = [{ label: t("仓库代码"), value: "warehouseCode" }];

/**
 * 候选 1P 仓库匹配记录（/manage/warehousePlatformMapping/list 的 items[].mappings[]）。
 * 每条记录携带该 1P 仓库自身的地址/联系人/状态，用于与父行 VC 仓库做地址对比。
 */
export interface WarehousePlatformMappingItemApi {
    mappingId: number;
    corpId: string;
    corpName: string;
    externalWarehouseId: string;
    externalWarehouseCode: string;
    externalWarehouseName: string;
    distance: string;
    status: number;
    statusLabel: string;
    matchTag: string;
    matchReason: string;
    remark: string;
    // ── 1P 仓库自身地址/联系人 ──
    regionCode: string;
    regionLabel: string;
    countryCode: string;
    countryLabel: string;
    state: string;
    stateLabel: string;
    zoneCode: string;
    zoneLabel: string;
    city: string;
    cityLabel: string;
    district: string;
    address1: string;
    address2: string;
    zipCode: string;
    contactName: string;
    contactPhone: string;
    contactEmail: string;
}

/** VC 仓库 + 候选 1P 仓库映射列表项（/manage/warehousePlatformMapping/list 的 items[]） */
export interface WarehousePlatformMappingApiItem {
    platformWarehouseId: string;
    platformWarehouseCode: string;
    platformWarehouseName: string;
    warehouseStatus: number;
    warehouseStatusLabel: string;
    platformStoreId: string;
    storeName: string;
    regionCode: string;
    regionLabel: string;
    countryCode: string;
    stateCode: string;
    zoneCode: string;
    zoneLabel: string;
    cityId: number;
    cityName: string;
    district: string;
    address1: string;
    address2: string;
    contactName: string;
    contactPhone: string;
    contactEmail: string;
    zipCode: string;
    mappings: WarehousePlatformMappingItemApi[];
}

/**
 * 映射子行（候选 1P），同时携带父带（VC 仓库）字段，
 * 供 useGroupHeaderTable 按 warehouseId 分组并生成组头带。
 */
export interface VcWarehouseMappingChildRow {
    /** 映射行 id；无匹配占位行为 `ph-{warehouseId}` */
    id: string | number;
    warehouseKind: VcWarehouseMappingKind;
    /** 无候选匹配时的占位行（仅用于展示 VC 仓组头） */
    isPlaceholder?: boolean;

    // ── 父带（VC 仓库汇总）字段 ──
    warehouseId: string;
    warehouseCode: string;
    warehouseName: string;
    warehouseStatus: number;
    warehouseStatusLabel: string;
    storeName: string;
    groupRegion: string;
    groupCountry: string;
    groupProvince: string;
    groupZone: string;
    groupCity: string;
    groupDistrict: string;
    groupAddress1: string;
    groupAddress2: string;
    groupZipCode: string;
    groupContactName: string;
    groupContactPhone: string;
    groupContactEmail: string;

    // ── 子行（候选 1P）字段 ──
    mappingId?: number;
    corpName: string;
    status: number;
    statusLabel: string;
    matchedWarehouseCode: string;
    matchedWarehouseName: string;
    distance: string;
    matchTag: string;
    matchReason: string;
    remark: string;
    region: string;
    country: string;
    province: string;
    zone: string;
    city: string;
    district: string;
    address1: string;
    address2: string;
    zipCode: string;
    contactName: string;
    contactPhone: string;
    contactEmail: string;
}

/** 组头带行（由 buildHeader 生成） */
export type VcWarehouseMappingGroupRow = TableGroupHeaderRow & {
    /** PO/DF，添加映射时随行传给 add 接口的 channelType */
    warehouseKind: VcWarehouseMappingKind;
    warehouseId: string;
    warehouseCode: string;
    warehouseName: string;
    warehouseStatus: number;
    warehouseStatusLabel: string;
    storeName: string;
    groupRegion: string;
    groupCountry: string;
    groupProvince: string;
    groupZone: string;
    groupCity: string;
    groupDistrict: string;
    groupAddress1: string;
    groupAddress2: string;
    groupZipCode: string;
    groupContactName: string;
    groupContactPhone: string;
    groupContactEmail: string;
    childCount: number;
};

/** 组头行操作 */
export function getVcWarehouseMappingGroupActions(): TableRowActionItem[] {
    return [{ label: t("编辑映射关系"), key: t("编辑映射关系") }];
}

/** 子行默认可见条数，超出后折叠（对齐 shipment） */
export const VC_WAREHOUSE_MAPPING_CHILD_VISIBLE_LIMIT = 3;

function buildGroupFields(item: WarehousePlatformMappingApiItem, warehouseKind: VcWarehouseMappingKind) {
    return {
        warehouseKind,
        warehouseId: item.platformWarehouseId,
        warehouseCode: item.platformWarehouseCode,
        warehouseName: item.platformWarehouseName,
        warehouseStatus: item.warehouseStatus,
        warehouseStatusLabel: item.warehouseStatusLabel,
        storeName: item.storeName,
        groupRegion: item.regionLabel || item.regionCode,
        groupCountry: item.countryCode,
        groupProvince: item.stateCode,
        groupZone: item.zoneLabel || item.zoneCode,
        groupCity: item.cityName,
        groupDistrict: item.district,
        groupAddress1: item.address1,
        groupAddress2: item.address2,
        groupZipCode: item.zipCode,
        groupContactName: item.contactName,
        groupContactPhone: item.contactPhone,
        groupContactEmail: item.contactEmail,
    };
}

function emptyChildFields(): Pick<
    VcWarehouseMappingChildRow,
    | "corpName"
    | "status"
    | "statusLabel"
    | "matchedWarehouseCode"
    | "matchedWarehouseName"
    | "distance"
    | "matchTag"
    | "matchReason"
    | "remark"
    | "region"
    | "country"
    | "province"
    | "zone"
    | "city"
    | "district"
    | "address1"
    | "address2"
    | "zipCode"
    | "contactName"
    | "contactPhone"
    | "contactEmail"
> {
    return {
        corpName: "",
        status: 0,
        statusLabel: "",
        matchedWarehouseCode: "",
        matchedWarehouseName: "",
        distance: "",
        matchTag: "",
        matchReason: "",
        remark: "",
        region: "",
        country: "",
        province: "",
        zone: "",
        city: "",
        district: "",
        address1: "",
        address2: "",
        zipCode: "",
        contactName: "",
        contactPhone: "",
        contactEmail: "",
    };
}

/** 将接口返回的单个 VC 仓库分组展平为候选 1P 子行（无匹配时回传占位子行以保留组头） */
export function mapWarehousePlatformMappingItem(item: WarehousePlatformMappingApiItem, warehouseKind: VcWarehouseMappingKind): VcWarehouseMappingChildRow[] {
    const groupFields = buildGroupFields(item, warehouseKind);
    const mappings = item.mappings ?? [];

    if (!mappings.length) {
        const warehouseId = item.platformWarehouseId;
        return [
            {
                id: `ph-${warehouseId}`,
                isPlaceholder: true,
                ...groupFields,
                ...emptyChildFields(),
            },
        ];
    }

    return mappings.map(mapping => ({
        id: mapping.mappingId,
        mappingId: mapping.mappingId,
        ...groupFields,
        corpName: mapping.corpName,
        status: mapping.status,
        statusLabel: mapping.statusLabel,
        matchedWarehouseCode: mapping.externalWarehouseCode,
        matchedWarehouseName: mapping.externalWarehouseName,
        distance: mapping.distance,
        matchTag: mapping.matchTag,
        matchReason: mapping.matchReason,
        remark: mapping.remark,
        region: mapping.regionLabel || mapping.regionCode,
        country: mapping.countryCode,
        province: mapping.state,
        zone: mapping.zoneLabel || mapping.zoneCode,
        city: mapping.city || mapping.cityLabel,
        district: mapping.district,
        address1: mapping.address1,
        address2: mapping.address2,
        zipCode: mapping.zipCode,
        contactName: mapping.contactName,
        contactPhone: mapping.contactPhone,
        contactEmail: mapping.contactEmail,
    }));
}
